import { createFileRoute, Outlet, redirect, useMatches, useRouter } from '@tanstack/react-router';
import { Layout, Menu, type MenuProps, Modal } from 'antd';
import { DashboardOutlined, LogoutOutlined, ShopOutlined, UserOutlined } from '@ant-design/icons';

import { useAuth } from '@/context/auth';
import { isAuthenticated } from '@/services/auth';

const { Sider, Content } = Layout;

export const Route = createFileRoute('/_auth')({
  beforeLoad: ({ location }) => {
    if (!isAuthenticated()) {
      throw redirect({
        to: '/login',
        search: {
          redirect: location.href,
        },
      });
    }
  },
  component: AuthLayout,
});

function AuthLayout() {
  const router = useRouter();
  const navigate = Route.useNavigate();
  const auth = useAuth();
  const matches = useMatches();

  // Get the last match which represents the current route
  const currentRoute = matches[matches.length - 1];
  const selectedKey = currentRoute?.pathname.split('/')[1] || 'dashboard';

  const handleLogout = () => {
    Modal.confirm({
      content: 'Are you sure you want to logout?',
      onOk: async () => {
        await auth.logout();
        await router.invalidate().finally(() => {
          void navigate({ to: '/login' });
        });
      },
    });
  };

  const menuItems: MenuProps['items'] = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
      onClick: () => void navigate({ to: '/dashboard' }),
    },
    {
      key: 'users',
      icon: <UserOutlined />,
      label: 'Users',
      onClick: () => void navigate({ to: '/users' }),
    },
    {
      key: 'companies',
      icon: <ShopOutlined />,
      label: 'Companies',
      onClick: () => void navigate({ to: '/companies' }),
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      danger: true,
      onClick: () => void handleLogout(),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider width={250} className="px-2">
        <div style={{ height: 32, margin: 16 }} />
        <Menu
          theme="dark"
          className="gap-2 flex flex-col"
          selectedKeys={[selectedKey]}
          mode="inline"
          items={menuItems}
        />
      </Sider>
      <Layout>
        <Content style={{ margin: '24px 16px', padding: 24, background: '#fff' }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
